import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Upload, ExternalLink, Trash2, FileText, AlertCircle, CheckCircle, Shield, Building, Search, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import axios from 'axios';

const DocumentManagementContent = () => {
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [isCompetitorDocument, setIsCompetitorDocument] = useState(false);
  const [showCompetitorDocs, setShowCompetitorDocs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, currentFile: '' });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Charger la liste des fichiers
  const loadFiles = useCallback(async () => {
    setIsLoadingFiles(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/list_files`);
      // Structure: ["nom_fichier", "concurrent_flag", "upload_date"]
      const fileList = response.data.files || [];
      const parsedFiles = fileList.map(item => {
        if (Array.isArray(item) && item.length >= 3) {
          return {
            name: item[0],
            isCompetitor: item[1] === "1",
            uploadDate: item[2]
          };
        } else if (Array.isArray(item) && item.length >= 2) {
          // Compatibilité si pas de date
          return {
            name: item[0],
            isCompetitor: item[1] === "1",
            uploadDate: null
          };
        } else if (typeof item === 'string') {
          // Compatibilité avec l'ancien format
          return {
            name: item,
            isCompetitor: false,
            uploadDate: null
          };
        }
        return null;
      }).filter(Boolean);
      
      setFiles(parsedFiles);
      setFilteredFiles(parsedFiles);
    } catch (error) {
      console.error('Erreur lors du chargement des fichiers:', error);
      setUploadStatus({ type: 'error', message: 'Erreur lors du chargement des fichiers' });
    } finally {
      setIsLoadingFiles(false);
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  // Filtrer les fichiers selon la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredFiles(files);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = files.filter(file => 
        file.name.toLowerCase().includes(query)
      );
      setFilteredFiles(filtered);
    }
  }, [searchQuery, files]);

  // Ajouter des fichiers à la file d'attente
  const handleFiles = useCallback((fileList) => {
    const newFiles = Array.from(fileList).filter(file => {
      // Vérifier le type de fichier
      if (file.type !== 'application/pdf') {
        setUploadStatus({ type: 'error', message: `"${file.name}" ignoré : seuls les PDF sont acceptés` });
        return false;
      }
      // Vérifier si le fichier n'est pas déjà dans la liste
      if (pendingFiles.some(f => f.name === file.name && f.size === file.size)) {
        return false;
      }
      return true;
    });

    if (newFiles.length > 0) {
      setPendingFiles(prev => [...prev, ...newFiles]);
      setUploadStatus({ 
        type: 'success', 
        message: `${newFiles.length} fichier${newFiles.length > 1 ? 's' : ''} ajouté${newFiles.length > 1 ? 's' : ''} à la file d'attente` 
      });
    }
  }, [pendingFiles]);

  // Supprimer un fichier de la file d'attente
  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Uploader tous les fichiers en attente
  const uploadAllFiles = async () => {
    if (pendingFiles.length === 0) return;

    setIsUploading(true);
    setUploadStatus(null);
    setUploadProgress({ current: 0, total: pendingFiles.length, currentFile: '' });

    const results = {
      success: 0,
      failed: 0,
      errors: []
    };

    for (let i = 0; i < pendingFiles.length; i++) {
      const file = pendingFiles[i];
      setUploadProgress({ 
        current: i + 1, 
        total: pendingFiles.length, 
        currentFile: file.name 
      });

      try {
        const formData = new FormData();
        formData.append('file', file);
        const concurrentValue = isCompetitorDocument ? '1' : '0';
        formData.append('concurrent', concurrentValue);

        console.log(`📤 Upload ${i + 1}/${pendingFiles.length}: ${file.name}`);

        await axios.post(`${BACKEND_URL}/api/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 120000000 // 20 minutes
        });

        results.success++;
        console.log(`✅ Upload réussi: ${file.name}`);
      } catch (error) {
        results.failed++;
        results.errors.push(`${file.name}: ${error.response?.data?.detail || error.message}`);
        console.error(`❌ Erreur upload ${file.name}:`, error);
      }
    }

    // Afficher le résultat final
    if (results.failed === 0) {
      setUploadStatus({ 
        type: 'success', 
        message: `${results.success} fichier${results.success > 1 ? 's' : ''} uploadé${results.success > 1 ? 's' : ''} avec succès` 
      });
    } else {
      setUploadStatus({ 
        type: 'error', 
        message: `${results.success} réussi(s), ${results.failed} échoué(s). Erreurs: ${results.errors.join('; ')}` 
      });
    }

    setPendingFiles([]);
    setUploadProgress({ current: 0, total: 0, currentFile: '' });
    setIsUploading(false);
    setIsCompetitorDocument(false);
    loadFiles();
  };

  // Gestion du drag & drop
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  // Ouverture de fichier dans un nouvel onglet
  const handleOpenFile = (fileObj) => {
    try {
      const fileUrl = `${BACKEND_URL}/api/download?filename=${encodeURIComponent(fileObj.name)}`;
      window.open(fileUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Erreur ouverture:', error);
      setUploadStatus({ type: 'error', message: 'Erreur lors de l\'ouverture du fichier' });
    }
  };

  // Suppression de fichier
  const handleDelete = async (fileObj) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer "${fileObj.name}" ?`)) {
      return;
    }

    try {
      await axios.get(`${BACKEND_URL}/api/removeSource?filename=${encodeURIComponent(fileObj.name)}`);
      setUploadStatus({ type: 'success', message: `Fichier "${fileObj.name}" supprimé avec succès` });
      loadFiles(); // Recharger la liste des fichiers
    } catch (error) {
      console.error('Erreur suppression:', error);
      setUploadStatus({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  };

  return (
    <div className="space-y-8">
      {/* Status message */}
      {uploadStatus && (
        <div className={`p-4 rounded-lg flex items-center space-x-3 ${
          uploadStatus.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {uploadStatus.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span className="text-sm font-medium">{uploadStatus.message}</span>
        </div>
      )}

      {/* Zone d'upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-800">Upload de fichiers</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Switch pour document concurrent */}
          <div className="flex items-center space-x-3 p-4 bg-slate-50 rounded-lg border">
            <Switch
              id="competitor-switch"
              checked={isCompetitorDocument}
              onCheckedChange={setIsCompetitorDocument}
            />
            <Label htmlFor="competitor-switch" className="text-sm font-medium text-slate-700">
              Document concurrent
            </Label>
            <span className="text-xs text-slate-500 ml-2">
              ({isCompetitorDocument ? 'Activé' : 'Désactivé'})
            </span>
          </div>
          
          {/* Message d'information sur la limite de caractères */}
          <div className="flex items-start space-x-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              <strong>Important :</strong> Le nom du fichier (titre) doit contenir au maximum 255 caractères.
            </p>
          </div>
          
          <div
            className={`border-2 border-dashed rounded-lg p-4 sm:p-8 text-center transition-colors ${
              dragActive 
                ? 'border-cyan-500 bg-cyan-50' 
                : 'border-slate-300 hover:border-slate-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 mb-4">
              Glissez-déposez vos fichiers PDF ici ou
            </p>
            <input
              type="file"
              accept=".pdf"
              multiple
              onChange={(e) => handleFiles(e.target.files)}
              className="hidden"
              id="file-input"
              disabled={isUploading}
            />
            <label htmlFor="file-input">
              <Button
                type="button"
                disabled={isUploading}
                className="cursor-pointer bg-cyan-500 hover:bg-cyan-600 text-white"
                onClick={() => document.getElementById('file-input').click()}
              >
                {isUploading ? 'Upload en cours...' : 'Sélectionner des fichiers'}
              </Button>
            </label>
          </div>

          {/* Liste des fichiers en attente */}
          {pendingFiles.length > 0 && (
            <div className="mt-4 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-700">
                  Fichiers en attente ({pendingFiles.length})
                </h4>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setPendingFiles([])}
                  disabled={isUploading}
                  className="text-xs"
                >
                  Vider la liste
                </Button>
              </div>
              
              <div className="max-h-60 overflow-y-auto space-y-2">
                {pendingFiles.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <FileText className="w-4 h-4 text-slate-600" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removePendingFile(index)}
                      disabled={isUploading}
                      className="hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Progression de l'upload */}
              {isUploading && uploadProgress.total > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">
                      Upload en cours: {uploadProgress.current}/{uploadProgress.total}
                    </span>
                    <span className="text-xs text-blue-700">
                      {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-blue-700 truncate">
                    {uploadProgress.currentFile}
                  </p>
                </div>
              )}

              {/* Bouton pour uploader tous les fichiers */}
              <Button
                onClick={uploadAllFiles}
                disabled={isUploading || pendingFiles.length === 0}
                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Upload en cours...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Uploader {pendingFiles.length} fichier{pendingFiles.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Liste des fichiers */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <CardTitle className="text-base sm:text-lg font-semibold text-slate-800">Fichiers disponibles</CardTitle>
            <div className="flex items-center gap-3">
              <div className="flex items-center space-x-2">
                <Label htmlFor="filter-switch" className="text-sm text-slate-600">
                  {showCompetitorDocs ? 'Concurrents' : 'Aprolis'}
                </Label>
                <Switch
                  id="filter-switch"
                  checked={showCompetitorDocs}
                  onCheckedChange={setShowCompetitorDocs}
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadFiles}
                disabled={isLoadingFiles}
                className="text-sm flex-shrink-0"
              >
                {isLoadingFiles ? 'Chargement...' : 'Actualiser'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Barre de recherche */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un document par nom..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            {searchQuery && (
              <p className="text-xs text-slate-500 mt-2">
                {filteredFiles.length} résultat{filteredFiles.length > 1 ? 's' : ''} trouvé{filteredFiles.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {isLoadingFiles ? (
            <div className="text-center py-8 text-slate-500">
              Chargement des fichiers...
            </div>
          ) : filteredFiles.length === 0 && !searchQuery ? (
            <div className="text-center py-8 text-slate-500">
              Aucun fichier disponible
            </div>
          ) : (
            <div className="space-y-6">
              {/* Documents standards */}
              {(() => {
                const standardDocs = filteredFiles.filter(file => !file.isCompetitor);
                return !showCompetitorDocs && standardDocs.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <Shield className="w-4 h-4 text-blue-600" />
                      <h4 className="text-sm font-semibold text-slate-700">
                        Documents standards ({standardDocs.length})
                      </h4>
                      <Badge variant="secondary" className="bg-blue-100 text-blue-700 border-blue-200">
                        Aprolis
                      </Badge>
                    </div>
                    <div className="grid gap-2">
                      {standardDocs.map((file, index) => (
                        <Card key={`standard-${index}`} className="border border-blue-200 bg-blue-50/30">
                          <CardContent className="p-2.5 sm:p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs sm:text-sm font-medium text-slate-800 truncate block">
                                    {file.name}
                                  </span>
                                  <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                                    <Badge variant="outline" className="text-[10px] sm:text-xs bg-blue-50 border-blue-200 text-blue-700">
                                      Aprolis
                                    </Badge>
                                    {file.uploadDate && (
                                      <span className="text-[10px] sm:text-xs text-slate-500 hidden sm:inline">
                                        {new Date(file.uploadDate).toLocaleDateString('fr-FR', { 
                                          year: 'numeric', 
                                          month: 'short', 
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenFile(file)}
                                  className="p-2 hover:bg-blue-100 border-blue-200"
                                  title="Ouvrir dans un nouvel onglet"
                                >
                                  <ExternalLink className="w-4 h-4 text-blue-600" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(file)}
                                  className="p-2 hover:bg-red-50 hover:border-red-200"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
              {/* Documents concurrents */}
              {(() => {
                const competitorDocs = filteredFiles.filter(file => file.isCompetitor);
                return showCompetitorDocs && competitorDocs.length > 0 && (
                  <div>
                    <div className="flex items-center space-x-2 mb-3">
                      <Building className="w-4 h-4 text-orange-600" />
                      <h4 className="text-sm font-semibold text-slate-700">
                        Documents concurrents ({competitorDocs.length})
                      </h4>
                      <Badge variant="secondary" className="bg-orange-100 text-orange-700 border-orange-200">
                        Concurrence
                      </Badge>
                    </div>
                    <div className="grid gap-2">
                      {competitorDocs.map((file, index) => (
                        <Card key={`competitor-${index}`} className="border border-orange-200 bg-orange-50/30">
                          <CardContent className="p-2.5 sm:p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                  <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-600" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <span className="text-xs sm:text-sm font-medium text-slate-800 truncate block">
                                    {file.name}
                                  </span>
                                  <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                                    <Badge variant="outline" className="text-[10px] sm:text-xs bg-orange-50 border-orange-200 text-orange-700">
                                      Concurrent
                                    </Badge>
                                    {file.uploadDate && (
                                      <span className="text-[10px] sm:text-xs text-slate-500 hidden sm:inline">
                                        {new Date(file.uploadDate).toLocaleDateString('fr-FR', { 
                                          year: 'numeric', 
                                          month: 'short', 
                                          day: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenFile(file)}
                                  className="p-2 hover:bg-orange-100 border-orange-200"
                                  title="Ouvrir dans un nouvel onglet"
                                >
                                  <ExternalLink className="w-4 h-4 text-orange-600" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(file)}
                                  className="p-2 hover:bg-red-50 hover:border-red-200"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })()}
              
              {/* Message si aucun document dans la catégorie sélectionnée ou recherche */}
              {(() => {
                const standardDocs = filteredFiles.filter(file => !file.isCompetitor);
                const competitorDocs = filteredFiles.filter(file => file.isCompetitor);
                
                // Message pour recherche sans résultat
                if (searchQuery && filteredFiles.length === 0) {
                  return (
                    <div className="text-center py-8 text-slate-500">
                      <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Aucun document trouvé pour "{searchQuery}"</p>
                      <button 
                        onClick={() => setSearchQuery('')} 
                        className="text-cyan-600 hover:underline text-sm mt-2"
                      >
                        Réinitialiser la recherche
                      </button>
                    </div>
                  );
                }
                
                // Messages pour catégorie vide
                if (!showCompetitorDocs && standardDocs.length === 0 && competitorDocs.length > 0) {
                  return (
                    <div className="text-center py-8 text-slate-500">
                      Aucun document Aprolis{searchQuery ? ' correspondant à votre recherche' : ''}. <button onClick={() => setShowCompetitorDocs(true)} className="text-cyan-600 hover:underline">Voir les documents concurrents</button>
                    </div>
                  );
                }
                
                if (showCompetitorDocs && competitorDocs.length === 0 && standardDocs.length > 0) {
                  return (
                    <div className="text-center py-8 text-slate-500">
                      Aucun document concurrent{searchQuery ? ' correspondant à votre recherche' : ''}. <button onClick={() => setShowCompetitorDocs(false)} className="text-cyan-600 hover:underline">Voir les documents Aprolis</button>
                    </div>
                  );
                }
                
                return null;
              })()}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentManagementContent;